import React, { type FC, useMemo, RefObject, useState, useCallback, useEffect } from 'react'
import clsx from 'clsx'
import { useMediaQuery } from '@chakra-ui/react'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import ApplyV2CvStep2 from '@core/components/ApplyDialogV2/ApplyV2CvStep/ApplyV2CvStep2'
import ApplyV2ProfileStep2 from '@core/components/ApplyDialogV2/ApplyV2ProfileStep/ApplyV2ProfileStep2'
import ApplyV2SkillsStep from '@core/components/ApplyDialogV2/ApplyV2SkillsStep'
import ApplyV2ReferenceStep2 from '@core/components/ApplyDialogV2/ApplyV2ReferenceStep/ApplyV2ReferenceStep2'
import ApplyV2ReviewApplyStep from '@core/components/ApplyDialogV2/ApplyV2ReviewApplyStep'
import ApplyV2EducationStep2 from '@core/components/ApplyDialogV2/ApplyV2EducationStep/ApplyV2EducationStep2/ApplyV2EducationStep2'
import ApplyV2WorkExperienceStep2 from '@core/components/ApplyDialogV2/ApplyV2WorkExperienceStep/ApplyV2WorkExperienceStep2'
import ApplyV2LanguageStep from '@core/components/ApplyDialogV2/ApplyV2LanguageStep'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2DriverLicenseStep from '@core/components/ApplyDialogV2/ApplyV2DriverLicenseStep'
import ApplyV2QuestionsStep from '@core/components/ApplyDialogV2/ApplyV2QuestionsStep'
import ApplyV2CoursesStep from '@core/components/ApplyDialogV2/ApplyV2CoursesStep'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Picture from '@core/components/ApplyDialogV2/ApplyV2Picture'
import type { ApplyV2SettingsSectionName, GetApplyV2SettingsResponse } from '@core/app/types/applyV2Type'
import './ApplyV2Content.scss'

const baseClass = 'ApplyV2Content'

interface ApplyV2ContentProps {
    className?: string
    navItems: {
        name: ApplyV2SettingsSectionName
        visible: boolean
        required: boolean
        order: number
    }[]
    // activeIndex: number
    currentStep: ApplyV2SettingsSectionName | null
    sliderRef: RefObject<HTMLDivElement>
    isExpanded: boolean
    isLoading: boolean
    settingsData: GetApplyV2SettingsResponse | null | undefined
    isFinalStep: boolean
}

const ApplyV2Content: FC<ApplyV2ContentProps> = ({
    className = '',
    navItems,
    currentStep,
    sliderRef,
    isExpanded,
    isLoading,
    settingsData,
    isFinalStep,
}) => {
    const [isDesktop] = useMediaQuery('(min-width: 1230px)')
    const { getTitle } = useApplyV2NavTranslations()
    const { isProfileDataLoading } = useApplyV2Context()
    const isBgImageOrColor = settingsData?.appearance?.image || settingsData?.appearance?.color
    const [scrollPosition, setScrollPosition] = useState<'top' | 'bottom' | 'none' | 'both'>('none')
    // const sliderRef = useRef<HTMLDivElement>(null)
    const stepValues = useMemo(() => {
        const activeIndex = navItems.findIndex((item) => item.name === currentStep)
        return `${activeIndex + 1}/${navItems.length}`
    }, [navItems, currentStep])

    const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
        const isAtTop = scrollTop < 30
        const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 30
        const hasScroll = scrollHeight > clientHeight

        if (!hasScroll) {
            setScrollPosition('none')
        } else if (isAtTop) {
            setScrollPosition('top')
        } else if (isAtBottom) {
            setScrollPosition('bottom')
        } else {
            setScrollPosition('both')
        }
    }, [])

    useEffect(() => {
        if (sliderRef.current) {
            const { scrollHeight, clientHeight } = sliderRef.current
            const hasScroll = scrollHeight > clientHeight

            if (!hasScroll) {
                setScrollPosition('none')
            } else if (sliderRef.current.scrollTop < 30) {
                setScrollPosition('top')
            } else if (Math.abs(scrollHeight - clientHeight - sliderRef.current.scrollTop) < 30) {
                setScrollPosition('bottom')
            } else {
                setScrollPosition('both')
            }
        }
    }, [currentStep, navItems])

    const getStepTitle = (step: ApplyV2SettingsSectionName | null) => {
        switch (step) {
            case 'course':
                return getTitle('10387', 'Courses / certificates')
            case 'language':
                return getTitle('3296', 'Languages')
            case 'cv':
                return getTitle('4845', 'Upload your CV')
            case 'driverLicence':
                const str = getTitle(step, 'Driver Licence')
                return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
            default:
                return getTitle(step ?? '', step ?? '')
        }
    }

    if (!isExpanded) {
        return (
            <div
                className={clsx(className, baseClass, {
                    [`${baseClass}--expanded`]: isExpanded,
                    [`${baseClass}--desktop`]: isDesktop,
                })}
            ></div>
        )
    }

    return (
        <div
            className={clsx(className, baseClass, {
                [`${baseClass}--expanded`]: isExpanded,
                [`${baseClass}--desktop`]: isDesktop,
                [`${baseClass}--desktop_no-image`]: isDesktop && !isBgImageOrColor,
            })}
            data-scrolled={scrollPosition}
        >
            <div className={`${baseClass}__slider-wrapper`} ref={sliderRef} onScroll={handleScroll}>
                {isProfileDataLoading ? (
                    <div className={`${baseClass}__slide`}>
                        <div className={`${baseClass}__slide__container`}>
                            {!(currentStep === 'reviewApply') && (
                                // {!(currentStep === 'reviewApply') && (
                                <>
                                    <ApplySkeleton isLoading={isLoading} height={18}>
                                        {
                                            <div className="ApplyV2Content__step">
                                                {getTitle('6593', 'Step')} {stepValues}
                                            </div>
                                        }
                                    </ApplySkeleton>
                                    <ApplySkeleton isLoading={isLoading} width={150} height={24}>
                                        <div className="ApplyV2Content__step-name">{getStepTitle(currentStep)}</div>
                                    </ApplySkeleton>
                                </>
                            )}
                            <ApplySkeleton
                                style={{ width: '100%', height: '100px', marginBottom: '32px' }}
                                isLoading={true}
                            />
                            <ApplySkeleton
                                style={{ width: '100%', height: '50px', marginBottom: '32px' }}
                                isLoading={true}
                            />
                            <ApplySkeleton
                                style={{ width: '100%', height: '50px', marginBottom: '32px' }}
                                isLoading={true}
                            />
                            <div
                                style={{ width: '100%', display: 'flex', justifyContent: 'end', marginBottom: '32px' }}
                            >
                                <ApplySkeleton style={{ width: '80px', height: '40px' }} isLoading={true} />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={`${baseClass}__slide`}>
                        <div className={`${baseClass}__slide__container`}>
                            {!(currentStep === 'reviewApply') && (
                                // {!(currentStep === 'reviewApply') && (
                                <>
                                    <ApplySkeleton isLoading={isLoading} height={18}>
                                        {
                                            <div className="ApplyV2Content__step">
                                                {getTitle('6593', 'Step')} {stepValues}
                                            </div>
                                        }
                                    </ApplySkeleton>
                                    <ApplySkeleton isLoading={isLoading} width={150} height={24}>
                                        <div className="ApplyV2Content__step-name">{getStepTitle(currentStep)}</div>
                                    </ApplySkeleton>
                                </>
                            )}
                            {currentStep === 'cv' && (
                                <>
                                    {/*<ApplyV2CvStep currentStep={currentStep} navItems={navItems} />*/}
                                    <ApplyV2CvStep2 />
                                </>
                            )}
                            {currentStep === 'profile' && (
                                <>
                                    {/*<ApplyV2ProfileStep />*/}
                                    <ApplyV2ProfileStep2 />
                                </>
                            )}
                            {currentStep === 'course' && <ApplyV2CoursesStep />}
                            {currentStep === 'question' && <ApplyV2QuestionsStep />}
                            {currentStep === 'reviewApply' && <ApplyV2ReviewApplyStep settingsData={settingsData} />}
                            {currentStep === 'education' && (
                                <>
                                    {/*<ApplyV2EducationStep />*/}
                                    <ApplyV2EducationStep2 />
                                </>
                            )}
                            {currentStep === 'reference' && (
                                <>
                                    {/*<ApplyV2ReferenceStep />*/}
                                    <ApplyV2ReferenceStep2 />
                                </>
                            )}
                            {currentStep === 'workExperience' && (
                                <>
                                    {/*<ApplyV2WorkExperienceStep />*/}
                                    <ApplyV2WorkExperienceStep2 />
                                </>
                            )}
                            {currentStep === 'language' && <ApplyV2LanguageStep />}
                            {currentStep === 'skill' && <ApplyV2SkillsStep />}
                            {currentStep === 'driverLicence' && <ApplyV2DriverLicenseStep />}
                            <ApplySkeleton
                                isLoading={isLoading}
                                width={'100%'}
                                height={100}
                                style={{ marginBottom: '16px' }}
                            ></ApplySkeleton>
                            <ApplySkeleton isLoading={isLoading} width={'100%'} height={70}></ApplySkeleton>
                        </div>
                    </div>
                )}
            </div>
            {isDesktop &&
                (settingsData?.appearance?.image ? (
                    <ApplyV2Picture
                        className={`${baseClass}__apply-picture`}
                        imgClassName={`${baseClass}__apply-picture-img`}
                        // imgSrcSet={`${bg1} 1x, ${bg2} 2x`}
                        // sources={[
                        //     {
                        //         srcSet: bg2Avif,
                        //         type: 'image/avif',
                        //         media: '(min-width: 1024px)',
                        //     },
                        //     {
                        //         srcSet: bg1Avif,
                        //         type: 'image/avif',
                        //         media: '(min-width: 768px) and (max-width: 1023px)',
                        //     },
                        //     {
                        //         srcSet: bgSizeAvif,
                        //         type: 'image/avif',
                        //         media: '(max-width: 767px)',
                        //     },
                        //     {
                        //         srcSet: `${bg2Webp}`,
                        //         type: 'image/webp',
                        //         media: '(min-width: 1024px)',
                        //     },
                        //     {
                        //         srcSet: `${bg1Webp}`,
                        //         type: 'image/webp',
                        //         media: '(min-width: 768px) and (max-width: 1023px)',
                        //     },
                        //     {
                        //         srcSet: `${bgSizeWebp}`,
                        //         type: 'image/webp',
                        //         media: '(max-width: 767px)',
                        //     },
                        //     {
                        //         srcSet: `${bg2}`,
                        //         type: 'image/jpeg',
                        //         media: '(min-width: 1024px)',
                        //     },
                        //     {
                        //         srcSet: `${bg1}`,
                        //         type: 'image/jpeg',
                        //         media: '(min-width: 768px) and (max-width: 1023px)',
                        //     },
                        //     {
                        //         srcSet: `${bgSize}`,
                        //         type: 'image/jpeg',
                        //         media: '(max-width: 767px)',
                        //     },
                        // ]}
                        loading="lazy"
                        fallbackSrc={settingsData.appearance.image}
                        alt={'apply bg'}
                    />
                ) : (
                    <div
                        className={`${baseClass}__bg`}
                        style={{
                            backgroundColor: settingsData?.appearance?.color || 'transparent',
                            backgroundImage: settingsData?.appearance?.image
                                ? `url(${settingsData?.appearance?.image})`
                                : 'none',
                        }}
                    ></div>
                ))}
        </div>
    )
}

export default ApplyV2Content
